import {
  Box,
  Button,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  Grid,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from "@mui/material";
import { Dispatch, SetStateAction, useState } from "react";
import { DiscoverySource, DiscoverySourceDisplayName } from "schemas/dashboard";
import { CustomEvent, Map } from "schemas/functions";
import { routes } from "schemas/routes";

import BentoBrandsAutocomplete from "components/BentoBrandsAutocomplete";
import LocationAutocomplete from "components/LocationAutocomplete";
import { UserLocation } from "components/LocationAutocomplete/schema";
import { isPage } from "utils/general";
import { makeDeepCopy } from "utils/updateLocalState";

import AboutFormLinks from "./Links";
import {
  SocialMediaFields,
  SocialMediaLinks,
  getUserType,
  isValidUsername,
  sanitizeInput,
} from "./helpers";
import styles from "./styles";

interface AboutFormProps {
  handleNext: (onboardingForm: Map) => void;
  handleSkip?: () => void;
  onboardingForm: Map;
  setOnboardingForm: Dispatch<SetStateAction<Map>>;
  isProfilePage?: boolean;
  isVAFlow?: boolean;
  hideDiscoveryQuestion?: boolean;
}

const AboutForm = ({
  handleNext,
  handleSkip,
  onboardingForm,
  setOnboardingForm,
  isProfilePage = false,
  isVAFlow = false,
  hideDiscoveryQuestion = false,
}: AboutFormProps) => {
  const [linksValidation, setLinksValidation] = useState<{
    [key in string]: string | undefined;
  }>({});
  const [isUgcValidation, setIsUgcValidation] = useState<string | undefined>();
  const [links, setLinks] = useState<SocialMediaLinks[]>([]);
  const [dreamBrandValidation, setDreamBrandValidation] = useState<
    string | undefined
  >();

  const handleChange = (e: CustomEvent) => {
    const { name, value } = e.target;
    const copy = makeDeepCopy(onboardingForm);
    copy[name] = value;
    setOnboardingForm(copy);
  };

  const updateWishlistBrands = (newBrands: string[]) => {
    setOnboardingForm((prev) => {
      return {
        ...prev,
        wishlistBrands: newBrands,
      };
    });
    if (newBrands.length > 0) {
      setDreamBrandValidation(undefined);
    }
  };

  const handleUpdateUserType = (e: CustomEvent) => {
    const { value } = e.target;
    const copy = makeDeepCopy(onboardingForm);
    if (value === "both") {
      copy.isUgcCreator = true;
      copy.isInfluencer = true;
    } else if (value === "ugc") {
      copy.isUgcCreator = true;
      copy.isInfluencer = false;
    } else if (value === "influencer") {
      copy.isInfluencer = true;
      copy.isUgcCreator = false;
    }
    copy.userType = value;
    setIsUgcValidation(undefined);
    setOnboardingForm(copy);
  };

  const handleLocationChange = (userLocation: UserLocation) => {
    setOnboardingForm((prev) => ({
      ...prev,
      city: userLocation.address,
      location: userLocation.country_code,
      addressComponents: userLocation.address_components,
      longitude: userLocation.longitude,
      latitude: userLocation.latitude,
    }));
  };

  const handleSubmit = (event: CustomEvent) => {
    event.preventDefault();

    if (!onboardingForm.isUgcCreator && !onboardingForm.isInfluencer) {
      setIsUgcValidation("Please fill out this field.");
      return;
    }

    if (isProfilePage) {
      if (Object.values(linksValidation).some((v) => v !== undefined)) {
        return;
      }

      for (const key in onboardingForm) {
        if (key in SocialMediaFields && onboardingForm[key]) {
          const input = sanitizeInput(onboardingForm[key]);
          if (input && !isValidUsername(input) && key !== "portfolioUrl") {
            return;
          }
        }
      }
    }

    if (!isVAFlow && !isProfilePage) {
      if (
        !onboardingForm.wishlistBrands ||
        onboardingForm.wishlistBrands.length === 0
      ) {
        setDreamBrandValidation("Please fill out this field.");
        return;
      }
    }

    handleNext(onboardingForm);
  };

  return (
    <>
      <form onSubmit={handleSubmit}>
        <Typography variant="h3" sx={{ mb: 3, textAlign: "center" }}>
          <Box
            component="i"
            color="secondary.dark"
            sx={{ pr: 2 }}
            className="fa-solid fa-user"
          />
          Your Basics
        </Typography>

        <Grid container columnSpacing={2}>
          <Grid item xs={12} md={6}>
            <FormHelperText sx={styles.helperText}>Name * </FormHelperText>
            <TextField
              sx={styles.textField}
              fullWidth
              name="name"
              placeholder="First Last"
              onChange={handleChange}
              required
              value={onboardingForm?.name || ""}
            />
          </Grid>
          <Grid item xs={12} md={6} sx={styles.fieldContainer}>
            <FormHelperText sx={styles.helperText}>Location</FormHelperText>
            <LocationAutocomplete
              handleLocationChange={handleLocationChange}
              userLocation={{
                address: onboardingForm.city,
                longitude: onboardingForm.longitude,
                latitude: onboardingForm.latitude,
                country_code: onboardingForm.location,
                address_components: onboardingForm.addressComponents,
              }}
            />
            <FormHelperText>
              <em>
                specifics help us tailor recommendations, (e.g., zip code,
                neighborhood or city)
              </em>
            </FormHelperText>
          </Grid>
        </Grid>

        <Grid sx={styles.fieldContainer}>
          <FormHelperText error={!!isUgcValidation}>
            Are you a UGC creator, Influencer or Both? *
          </FormHelperText>
          <FormControl error={!!isUgcValidation}>
            <FormLabel id="ugc-creator-label"></FormLabel>
            <RadioGroup
              onChange={handleUpdateUserType}
              name="userType"
              row
              aria-labelledby="ugc-creator-label"
              value={onboardingForm?.userType || getUserType(onboardingForm)}
            >
              <FormControlLabel value={"ugc"} control={<Radio />} label="UGC" />
              <FormControlLabel
                value={"influencer"}
                control={<Radio />}
                label="Influencer"
              />
              <FormControlLabel
                value={"both"}
                control={<Radio />}
                label="Both"
              />
            </RadioGroup>
            {isUgcValidation && (
              <FormHelperText sx={{ ml: 0 }}>{isUgcValidation}</FormHelperText>
            )}
          </FormControl>
        </Grid>

        {(onboardingForm?.isUgcCreator || onboardingForm?.isInfluencer) &&
          isProfilePage && (
            <AboutFormLinks
              onboardingForm={onboardingForm}
              setOnboardingForm={setOnboardingForm}
              setLinksValidation={setLinksValidation}
              links={links}
              setLinks={setLinks}
              validation={linksValidation}
            />
          )}

        {!isProfilePage && !isVAFlow && (
          <Grid sx={styles.fieldContainer}>
            <FormHelperText
              error={!!dreamBrandValidation}
              sx={styles.helperText}
            >
              Enter a few dream brands you'd love to partner with *
            </FormHelperText>
            <BentoBrandsAutocomplete
              selectedBentoBrands={onboardingForm?.wishlistBrands || []}
              updateSelectedBentoBrands={updateWishlistBrands}
            />
            {dreamBrandValidation && (
              <FormHelperText sx={{ mt: -2 }} error={!!dreamBrandValidation}>
                {dreamBrandValidation}
              </FormHelperText>
            )}
          </Grid>
        )}

        {!hideDiscoveryQuestion && (
          <Grid sx={styles.fieldContainer}>
            <FormHelperText>How did you hear about us?</FormHelperText>
            <FormControl>
              <FormLabel id="discovery-source-label"></FormLabel>
              <RadioGroup
                onChange={handleChange}
                name="discoverySource"
                row
                aria-labelledby="discovery-source-label"
                value={onboardingForm?.discoverySource}
              >
                {Object.values(DiscoverySource)?.map((source) => (
                  <FormControlLabel
                    value={source}
                    control={<Radio />}
                    label={DiscoverySourceDisplayName[source]}
                    checked={onboardingForm?.discoverySource === source}
                    key={source}
                  />
                ))}
              </RadioGroup>
            </FormControl>

            {onboardingForm?.discoverySource === DiscoverySource.other && (
              <TextField
                sx={styles.textField}
                fullWidth
                name="discoverySourceOther"
                placeholder="Please specify"
                onChange={handleChange}
                value={onboardingForm?.discoverySourceOther || ""}
              />
            )}
          </Grid>
        )}

        {!isProfilePage && (
          <Grid container>
            <Grid item xs></Grid>
            {handleSkip && (
              <Grid item xs="auto">
                <Button onClick={handleSkip} color="secondary" disableElevation>
                  Skip
                </Button>
              </Grid>
            )}
            <Grid item xs="auto">
              <Button
                disableElevation
                variant={
                  isPage(routes.aboutSelfServeOnboarding)
                    ? undefined
                    : "contained"
                }
                type="submit"
              >
                Next
                {isPage(routes.aboutSelfServeOnboarding) && (
                  <Box
                    component="i"
                    className="fa-regular fa-arrow-right"
                    sx={{ ml: 1 }}
                  />
                )}
              </Button>
            </Grid>
          </Grid>
        )}
      </form>
    </>
  );
};

export default AboutForm;
